import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import { useExplorerStore } from "@/modules/explorer/store";
import DimensionTypes = App.Enums.DimensionTypes;
import { ActivePin } from "@/modules/explorer/explorerTypes";
import { ApiError } from "@/api/base";
import { t } from "@/plugins/i18n";

export const filterHasInvalidDataSource = (
	filter: ActiveFilterDTO,
	includeSecondaryVertical: boolean = false
): boolean => {
	const derivedDocument = useExplorerStore()?.derivedDocument;

	// Something is likely still loading -> don't show error state
	if (
		!filter.isApplied ||
		!derivedDocument?.vertical ||
		!filter?.dimension?.available_for_data_source
	) {
		return false;
	}

	const viewTab = useExplorerStore().document.view_tab;

	// For single-metric, also look at the data table metrics
	let dataSources: Set<string>;
	if (viewTab === "graph") {
		dataSources = new Set([
			...(derivedDocument.vertical.data_source ?? []),
			...(includeSecondaryVertical
				? derivedDocument.secondary_vertical?.data_source ?? []
				: []),
		]);
	} else {
		dataSources = new Set(
			derivedDocument.data_tables[0].metrics.flatMap(
				(metric) => metric.data_source
			)
		);
	}

	return !filter.dimension.available_for_data_source.some((filterDataSource) =>
		dataSources.has(filterDataSource)
	);
};

export const filterSupportsQuickFilter = (
	filter: ActiveFilterDTO | ActivePin
): boolean => {
	const allowed: DimensionTypes[] = ["nominal", "bucketed", "hierarchy"];
	return allowed.includes(filter.dimension.type as DimensionTypes);
};

/**
 * A document can outlive the hierarchy it filters on: the owner can make it private (403) or
 * delete it (404) after the document was saved or shared. Both surface as an empty list, so this
 * copy is all the user gets to explain why — anything else the call fails with gets the generic
 * line rather than silently rendering "no data".
 *
 * Laravel's own 404 body is not user-facing (`findOrFail` leaks the model name in debug mode),
 * so the status decides the copy rather than `error.message`.
 */
export const hierarchyLoadErrorMessage = (error: ApiError): string => {
	if (error.status === 403) {
		return t("explorer.hierarchy.error.private");
	}

	if (error.status === 404) {
		return t("explorer.hierarchy.error.notFound");
	}

	return t("explorer.hierarchy.error.generic");
};
